import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatCents } from '../lib/money';
import { useAuth } from '../lib/AuthContext.jsx';
import SubscriptionBuilder from '../components/SubscriptionBuilder.jsx';
import AddressFields from '../components/AddressFields.jsx';
import CardSetup from '../components/CardSetup.jsx';

function emptySubscription() {
  return {
    priceId: '',
    planTier: '',
    addonPriceIds: [],
    initialFeePriceId: null,
    initialFeeCents: 0,
    initialFeeDisplayName: null,
    extraFeePriceIds: [],
  };
}
function emptyAddress() {
  return { line1: '', line2: '', city: '', state: '', postalCode: '', country: 'US' };
}

function toStripeAddress(addr) {
  if (!addr || !addr.line1) return undefined;
  return { line1: addr.line1, line2: addr.line2 || undefined, city: addr.city, state: addr.state, postal_code: addr.postalCode, country: addr.country || 'US' };
}

export default function CreateOrder() {
  const navigate = useNavigate();
  const { user, isRep } = useAuth();
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reps, setReps] = useState([]);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  // Stage 1: customer + addresses
  const [stage, setStage] = useState('customer'); // 'customer' | 'payment' | 'plans'
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [billingAddress, setBillingAddress] = useState(emptyAddress());
  const [serviceAddress, setServiceAddress] = useState(emptyAddress());
  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [resolving, setResolving] = useState(false);

  // Stage 2: payment
  const [resolvedCustomerId, setResolvedCustomerId] = useState(null);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState(null);

  // Stage 3: plans + coupon
  const [attributedRepId, setAttributedRepId] = useState('');
  const [subscriptions, setSubscriptions] = useState([emptySubscription()]);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const selectedCoupon = coupons.find((c) => c.id === selectedCouponId) || null;
  const [showManualCoupon, setShowManualCoupon] = useState(false);
  const [manualCouponCode, setManualCouponCode] = useState('');
  const [manualCouponStatus, setManualCouponStatus] = useState('idle'); // 'idle' | 'checking' | 'valid' | 'invalid'
  const [manualCouponInfo, setManualCouponInfo] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/products').then((r) => setProducts(r.products)).catch((e) => setError(e.message));
    api.get('/products/coupons').then((r) => setCoupons(r.coupons)).catch(() => {});
    // Reps aren't granted GET /reps (see backend/src/routes/reps.js) and
    // don't need the roster -- their own attribution is forced server-side
    // (see orders.js) and shown below as a fixed line, not this dropdown.
    if (!isRep) {
      api.get('/reps').then((r) => setReps(r.reps)).catch(() => {});
    }
  }, [isRep]);

  function resetAll() {
    setRequestId(crypto.randomUUID());
    setStage('customer');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setBillingAddress(emptyAddress());
    setServiceAddress(emptyAddress());
    setSameAsBilling(true);
    setSearching(false);
    setSearched(false);
    setMatches([]);
    setSelectedMatchId('');
    setResolvedCustomerId(null);
    setSetupClientSecret(null);
    setPaymentMethodId(null);
    setAttributedRepId('');
    setSubscriptions([emptySubscription()]);
    setSelectedCouponId('');
    setShowManualCoupon(false);
    setManualCouponCode('');
    setManualCouponStatus('idle');
    setManualCouponInfo(null);
    setResult(null);
    setError(null);
  }

  async function resolveCustomer(customerId) {
    setResolving(true);
    setError(null);
    try {
      const res = await api.post('/customers', {
        requestId,
        customerId: customerId || undefined,
        name: customerName,
        email: customerEmail,
        phone: customerPhone,
        billingAddress,
      });
      setResolvedCustomerId(res.stripeCustomerId);
      setSetupClientSecret(res.clientSecret);
      setStage('payment');
    } catch (err) {
      setError(err.message);
    } finally {
      setResolving(false);
    }
  }

  async function handleFindOrContinue(e) {
    e.preventDefault();
    setError(null);

    if (!searched) {
      setSearching(true);
      try {
        const { customers } = await api.get(`/customers/search?email=${encodeURIComponent(customerEmail)}`);
        setSearched(true);
        setSearching(false);
        if (customers.length > 0) {
          setMatches(customers);
          setSelectedMatchId(customers[0].id);
          return; // wait for the CSR to confirm which customer, below
        }
        await resolveCustomer(null);
      } catch (err) {
        setSearching(false);
        setError(err.message);
      }
      return;
    }

    await resolveCustomer(selectedMatchId || null);
  }

  function handleCardConfirmed(pmId) {
    setPaymentMethodId(pmId);
    setStage('plans');
  }

  async function applyManualCoupon() {
    if (!manualCouponCode) return;
    setManualCouponStatus('checking');
    try {
      const info = await api.get(`/products/coupon?code=${encodeURIComponent(manualCouponCode)}`);
      if (info.valid) {
        setManualCouponStatus('valid');
        setManualCouponInfo(info);
      } else {
        setManualCouponStatus('invalid');
        setManualCouponInfo(null);
      }
    } catch (err) {
      setManualCouponStatus('invalid');
      setManualCouponInfo(null);
    }
  }

  function updateSubscription(index, next) {
    setSubscriptions((subs) => subs.map((s, i) => (i === index ? next : s)));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = {
        requestId,
        stripeCustomerId: resolvedCustomerId,
        paymentMethodId,
        customerEmail,
        customerName,
        customerPhone,
        billingAddress,
        serviceAddress: sameAsBilling ? billingAddress : serviceAddress,
        couponCode: manualCouponStatus === 'valid' ? manualCouponCode : (selectedCoupon ? selectedCoupon.code : null),
        attributedRepId: attributedRepId || null,
        // priceId/addonPriceIds/extraFeePriceIds only -- every fee amount is
        // derived server-side (the plan's required fee from its
        // admin-configured link, extras via resolveExtraFees), never
        // trusted from the client.
        subscriptions: subscriptions.map((s) => ({
          priceId: s.priceId,
          planTier: s.planTier,
          addonPriceIds: s.addonPriceIds,
          extraFeePriceIds: s.extraFeePriceIds,
        })),
      };
      const res = await api.post('/orders', body);
      setResult(res);
      if (res.allSucceeded) {
        if (isRep) {
          // Reps don't have access to Order Detail (csr+ only, see
          // App.jsx's RequireCsr) -- the success banner above already
          // confirms it; reset to a blank form so they're ready for the
          // next customer at the next door rather than navigating
          // somewhere that would just bounce them back here.
          setTimeout(() => resetAll(), 1800);
        } else {
          setTimeout(() => navigate(`/orders/${res.results[0].order.id}`), 1200);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const canFindOrContinue = customerName && customerEmail && billingAddress.line1 && billingAddress.city && billingAddress.state && billingAddress.postalCode
    && (sameAsBilling || (serviceAddress.line1 && serviceAddress.city && serviceAddress.state && serviceAddress.postalCode));
  const canSubmit = subscriptions.every((s) => s.priceId) && !submitting;

  return (
    <div>
      <h2>New Sign Up</h2>
      {error && <div className="error-banner">{error}</div>}
      {result && (
        <div className={result.allSucceeded ? 'card' : 'error-banner'}>
          {result.allSucceeded
            ? 'Sign-up created successfully.'
            : `Some plans failed. Safe to retry this same form -- Stripe idempotency keys prevent duplicates. Details: ${JSON.stringify(result.results.filter((r) => !r.ok))}`}
        </div>
      )}

      {stage !== 'customer' && (
        <p>
          <a href="#" onClick={(e) => { e.preventDefault(); resetAll(); }}>← Start a different sign-up</a>
        </p>
      )}

      {stage === 'customer' && (
        <form onSubmit={handleFindOrContinue}>
          <div className="card">
            <span className="stage-label">Step 1 of 3</span>
            <h3 style={{ marginTop: 0 }}>Customer</h3>
            <div className="form-row">
              <div>
                <label>Full name</label>
                <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
              </div>
              <div>
                <label>Email</label>
                <input type="email" value={customerEmail} onChange={(e) => { setCustomerEmail(e.target.value); setSearched(false); setMatches([]); }} required />
              </div>
              <div>
                <label>Phone</label>
                <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
              </div>
            </div>

            <label>Sales credit (rep attribution)</label>
            {isRep ? (
              // Forced server-side to the logged-in rep's own id (see
              // orders.js) -- no dropdown, nothing for a rep to change.
              <p style={{ fontSize: 14, margin: '0 0 12px', color: 'var(--color-text)' }}>
                Credited to you ({user?.name}) automatically.
              </p>
            ) : (
              <select value={attributedRepId} onChange={(e) => setAttributedRepId(e.target.value)}>
                <option value="">No rep credit (organic / phone lead)</option>
                {reps.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="card">
            <AddressFields value={billingAddress} onChange={setBillingAddress} legend="Billing address" />
          </div>

          <div className="card">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" style={{ width: 'auto', marginBottom: 0 }} checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} />
              Service address is the same as billing
            </label>
            {!sameAsBilling && (
              <div style={{ marginTop: 12 }}>
                <AddressFields value={serviceAddress} onChange={setServiceAddress} legend="Service address (property being treated)" />
              </div>
            )}
          </div>

          {searched && matches.length > 0 && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>We found existing customer(s) with this email</h3>
              {matches.map((m) => (
                <label key={m.id} className="customer-match">
                  <input type="radio" name="match" checked={selectedMatchId === m.id} onChange={() => setSelectedMatchId(m.id)} />
                  <span>
                    <strong>{m.name || '(no name on file)'}</strong>
                    <span>{m.email} {m.phone ? `· ${m.phone}` : ''} · Stripe customer {m.id}</span>
                  </span>
                </label>
              ))}
              <label className="customer-match">
                <input type="radio" name="match" checked={selectedMatchId === ''} onChange={() => setSelectedMatchId('')} />
                <span>
                  <strong>None of these — create a new customer</strong>
                </span>
              </label>
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={!canFindOrContinue || searching || resolving}>
            {searching ? 'Checking for existing customer…' : resolving ? 'Setting up…' : searched ? 'Confirm & continue to payment' : 'Continue to payment'}
          </button>
        </form>
      )}

      {stage === 'payment' && (
        <div className="card">
          <span className="stage-label">Step 2 of 3</span>
          <h3 style={{ marginTop: 0 }}>Payment method</h3>
          <CardSetup
            clientSecret={setupClientSecret}
            billingDetails={{ name: customerName, email: customerEmail, phone: customerPhone || undefined, address: toStripeAddress(billingAddress) }}
            onConfirmed={handleCardConfirmed}
          />
        </div>
      )}

      {stage === 'plans' && (
        <form onSubmit={handleSubmit}>
          <span className="stage-label">Step 3 of 3</span>

          {subscriptions.map((sub, i) => (
            <SubscriptionBuilder
              key={i}
              products={products}
              value={sub}
              addedAsExtra={i > 0}
              onChange={(next) => updateSubscription(i, next)}
              onRemove={() => setSubscriptions((subs) => subs.filter((_, idx) => idx !== i))}
              canRemove={subscriptions.length > 1}
            />
          ))}

          <button type="button" className="btn" onClick={() => setSubscriptions((subs) => [...subs, emptySubscription()])}>
            + Add another plan
          </button>

          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0 }}>Coupon</h3>
            <label>Coupon code</label>
            <select
              value={selectedCouponId}
              disabled={showManualCoupon}
              onChange={(e) => setSelectedCouponId(e.target.value)}
            >
              <option value="">No coupon</option>
              {coupons.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayLabel} ({c.code}) — {c.percentOff ? `${c.percentOff}% off` : `${formatCents(c.amountOffCents)} off`}, {c.duration}
                </option>
              ))}
            </select>
            {selectedCoupon && !showManualCoupon && (
              <p style={{ color: 'var(--color-success)', fontSize: 13 }}>
                Applied: {selectedCoupon.code}
                {selectedCoupon.percentOff ? ` — ${selectedCoupon.percentOff}% off` : ''}
                {selectedCoupon.amountOffCents ? ` — ${formatCents(selectedCoupon.amountOffCents)} off` : ''}
                {' '}({selectedCoupon.duration})
              </p>
            )}

            {!showManualCoupon ? (
              <a
                href="#"
                style={{ fontSize: 13, display: 'inline-block', marginTop: 8 }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowManualCoupon(true);
                  setSelectedCouponId('');
                }}
              >
                Customer has a different code — enter it manually
              </a>
            ) : (
              <div style={{ marginTop: 12 }}>
                <div className="form-row" style={{ alignItems: 'flex-end' }}>
                  <div>
                    <label>Enter code manually</label>
                    <input
                      value={manualCouponCode}
                      onChange={(e) => { setManualCouponCode(e.target.value); setManualCouponStatus('idle'); setManualCouponInfo(null); }}
                      placeholder="e.g. the code the customer gave you"
                    />
                  </div>
                  <div style={{ flex: '0 0 auto', marginBottom: 12 }}>
                    <button type="button" className="btn" onClick={applyManualCoupon} disabled={!manualCouponCode || manualCouponStatus === 'checking'}>
                      {manualCouponStatus === 'checking' ? 'Checking…' : 'Apply'}
                    </button>
                  </div>
                </div>
                {manualCouponStatus === 'valid' && manualCouponInfo && (
                  <p style={{ color: 'var(--color-success)', fontSize: 13 }}>
                    Applied: {manualCouponInfo.code}
                    {manualCouponInfo.percentOff ? ` — ${manualCouponInfo.percentOff}% off` : ''}
                    {manualCouponInfo.amountOffCents ? ` — ${formatCents(manualCouponInfo.amountOffCents)} off` : ''}
                    {' '}({manualCouponInfo.duration})
                  </p>
                )}
                {manualCouponStatus === 'invalid' && (
                  <p style={{ color: 'var(--color-danger)', fontSize: 13 }}>Not a valid or active coupon code.</p>
                )}
                <a
                  href="#"
                  style={{ fontSize: 13, display: 'inline-block', marginTop: 4 }}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowManualCoupon(false);
                    setManualCouponCode('');
                    setManualCouponStatus('idle');
                    setManualCouponInfo(null);
                  }}
                >
                  ← Back to the coupon list
                </a>
              </div>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {submitting ? 'Submitting…' : 'Create sign-up'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
